/**
 * EntityHistoryFieldsContext — provides a fast O(1) lookup for "has this
 * field ever been changed on the current entity?".
 *
 * Hydrated in EntityDetail.tsx from the changelog query that is already
 * in-flight for the "Change history" section — no extra network cost.
 *
 * Pages that do NOT wrap their tree in EntityHistoryFieldsProvider (e.g.
 * Entities list, Renewals) fall through to the default context value which
 * always returns false → clock icons stay hidden on those pages.
 */

import { createContext, useContext, useMemo } from "react";
import type { ChangeLog } from "./types";

interface Ctx {
  hasField: (field: string) => boolean;
}

const EntityHistoryFieldsContext = createContext<Ctx>({ hasField: () => false });

export function EntityHistoryFieldsProvider({
  items,
  children,
}: {
  items: ChangeLog[];
  children: React.ReactNode;
}): JSX.Element {
  const value = useMemo<Ctx>(() => {
    const set = new Set(items.map((i) => i.field));
    return { hasField: (f) => set.has(f) };
  }, [items]);

  return (
    <EntityHistoryFieldsContext.Provider value={value}>
      {children}
    </EntityHistoryFieldsContext.Provider>
  );
}

export function useFieldHasHistory(field: string): boolean {
  return useContext(EntityHistoryFieldsContext).hasField(field);
}
